import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import InfluencerBudgetService from "../services/InfluencerBudgetService";
import { toastService } from '../services/toastService';
import { formatCurrency, getUtilizationColor } from './utils';

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-[400px]">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
  </div>
);

// Stat Card Component for Aggregated Data
const AggregateStatCard = ({ title, value, subtitle, icon, color }) => {
  const colorClasses = {
    blue: { bg: 'bg-blue-50', icon: 'bg-blue-100 text-blue-600', border: 'border-blue-200' },
    orange: { bg: 'bg-orange-50', icon: 'bg-orange-100 text-orange-600', border: 'border-orange-200' },
    purple: { bg: 'bg-purple-50', icon: 'bg-purple-100 text-purple-600', border: 'border-purple-200' },
    green: { bg: 'bg-green-50', icon: 'bg-green-100 text-green-600', border: 'border-green-200' },
  };

  const colors = colorClasses[color] || colorClasses.blue;

  const icons = {
    wallet: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
    cart: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    cash: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    rollover: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
    users: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
  };

  return (
    <div className="rounded-xl border p-6 transition-all duration-200 hover:shadow-md bg-white border-gray-200">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
          <p className="text-2xl font-bold truncate text-gray-900">
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ml-4 ${colors.icon}`}>
          {icons[icon]}
        </div>
      </div>
    </div>
  );
};

// Utilization Badge Component
const UtilizationBadge = ({ percentage }) => {
  const getConfig = () => {
    if (percentage < 50) return { label: 'Niska', bg: 'bg-green-100', text: 'text-green-800' };
    if (percentage < 80) return { label: 'Srednja', bg: 'bg-yellow-100', text: 'text-yellow-800' };
    return { label: 'Visoka', bg: 'bg-red-100', text: 'text-red-800' };
  };
  const config = getConfig();
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label} ({percentage.toFixed(1)}%)
    </span>
  );
};

// Format yearMonth for display
const formatYearMonth = (yearMonth) => {
  if (!yearMonth) return '';
  const [year, month] = yearMonth.split('-');
  const monthNames = ['Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun', 'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'];
  return `${monthNames[parseInt(month) - 1]} ${year}`;
};

// Filter Controls Component
const FilterControls = ({
  yearMonths,
  selectedMonth,
  onMonthChange,
  searchTerm,
  onSearchChange,
  utilizationFilter,
  onUtilizationChange
}) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Month Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mesec</label>
          <select
            value={selectedMonth}
            onChange={(e) => onMonthChange(e.target.value)}
            className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {yearMonths.map(month => (
              <option key={month} value={month}>{formatYearMonth(month)}</option>
            ))}
          </select>
        </div>

        {/* Search Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Pretraga po imenu</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Unesite ime influensera..."
            className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Utilization Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Iskoriscenost</label>
          <select
            value={utilizationFilter}
            onChange={(e) => onUtilizationChange(e.target.value)}
            className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Sve</option>
            <option value="low">Niska (&lt;50%)</option>
            <option value="medium">Srednja (50-80%)</option>
            <option value="high">Visoka (&gt;80%)</option>
          </select>
        </div>
      </div>
    </div>
  );
};

// Influencer Budget Table Component
const InfluencerBudgetTable = ({ budgets, onRowClick }) => {
  if (!budgets || budgets.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-center h-[200px] text-gray-400">
          <div className="text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <p>Nema influensera koji odgovaraju filterima</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-gray-600 border-b border-gray-200">
              <th className="px-4 py-3 font-medium">Influenser</th>
              <th className="px-4 py-3 font-medium text-right">Dodeljeno</th>
              <th className="px-4 py-3 font-medium text-right">Potroseno</th>
              <th className="px-4 py-3 font-medium text-right">Preneto</th>
              <th className="px-4 py-3 font-medium text-right">Dostupno</th>
              <th className="px-4 py-3 font-medium text-center">Iskoriscenost</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {budgets.map((budget) => {
              const utilization = budget.allocatedAmount > 0
                ? (budget.spentAmount / budget.allocatedAmount) * 100
                : 0;
              return (
                <tr
                  key={budget.customerId}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => onRowClick && onRowClick(budget.customerId)}
                >
                  <td className="px-4 py-3 font-medium text-gray-900">{budget.customerName}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{formatCurrency(budget.allocatedAmount)} RSD</td>
                  <td className="px-4 py-3 text-right text-gray-600">{formatCurrency(budget.spentAmount)} RSD</td>
                  <td className="px-4 py-3 text-right text-gray-600">{formatCurrency(budget.rolloverAmount)} RSD</td>
                  <td className="px-4 py-3 text-right font-medium text-purple-600">
                    {formatCurrency(budget.availableBudget)} RSD
                  </td>
                  <td className="px-4 py-3 text-center">
                    <UtilizationBadge percentage={utilization} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Main Admin Budget Dashboard Component
const AdminBudgetDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [yearMonths, setYearMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [utilizationFilter, setUtilizationFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Load available months on mount
  useEffect(() => {
    loadAvailableMonths();
  }, []);

  // Load data when month changes
  useEffect(() => {
    if (selectedMonth) {
      loadBudgetOverview();
    }
  }, [selectedMonth]);

  // Debounced filter effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (selectedMonth) {
        loadBudgetOverview();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, utilizationFilter]);

  const loadAvailableMonths = async () => {
    try {
      const response = await InfluencerBudgetService.getAvailableYearMonths();
      const months = response.data || [];
      setYearMonths(months);
      if (months.length > 0) {
        setSelectedMonth(months[0]); // Default to most recent (current month)
      }
    } catch (error) {
      toastService.error('Greska pri ucitavanju meseci');
      console.error('Error loading months:', error);
    }
  };

  const loadBudgetOverview = async () => {
    try {
      setLoading(true);
      const filter = {
        yearMonth: selectedMonth,
        influencerName: searchTerm || null,
        utilizationLevel: utilizationFilter || null
      };
      const response = await InfluencerBudgetService.getAdminBudgetOverviewFiltered(filter);
      setOverview(response.data);
    } catch (error) {
      toastService.error('Greska pri ucitavanju podataka');
      console.error('Error loading overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (customerId) => {
    // Navigate to client edit page to view/manage influencer details
    navigate(`/clients/edit/${customerId}`);
  };

  // Calculate overall utilization
  const overallUtilization = useMemo(() => {
    if (!overview || overview.totalAllocated === 0) return 0;
    return (overview.totalSpent / overview.totalAllocated) * 100;
  }, [overview]);

  if (loading && !overview) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Budzet Influensera - Pregled</h1>
        <p className="text-gray-500 mt-1">Agregirani podaci o budzetima svih influensera</p>
      </div>

      {/* Filter Controls */}
      <FilterControls
        yearMonths={yearMonths}
        selectedMonth={selectedMonth}
        onMonthChange={setSelectedMonth}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        utilizationFilter={utilizationFilter}
        onUtilizationChange={setUtilizationFilter}
      />

      {/* Loading overlay for filter changes */}
      {loading && overview && (
        <div className="fixed inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Aggregate Stats */}
      {overview && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <AggregateStatCard
              title="Ukupno Dodeljeno"
              value={`${formatCurrency(overview.totalAllocated)} RSD`}
              subtitle={`${overview.influencerCount} influensera`}
              icon="wallet"
              color="blue"
            />
            <AggregateStatCard
              title="Ukupno Potroseno"
              value={`${formatCurrency(overview.totalSpent)} RSD`}
              subtitle={`${overallUtilization.toFixed(1)}% iskorisceno`}
              icon="cart"
              color="orange"
            />
            <AggregateStatCard
              title="Ukupno Dostupno"
              value={`${formatCurrency(overview.totalAvailable)} RSD`}
              subtitle="Preostalo"
              icon="cash"
              color="purple"
            />
            <AggregateStatCard
              title="Ukupno Preneto"
              value={`${formatCurrency(overview.totalRollover)} RSD`}
              subtitle="Iz prethodnog meseca"
              icon="rollover"
              color="green"
            />
          </div>

          {/* Overall Progress Bar */}
          {overview.totalAllocated > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-medium text-gray-600">Ukupna iskoriscenost budzeta</span>
                <span className={`text-lg font-bold ${
                  overallUtilization < 50 ? 'text-green-600' :
                  overallUtilization < 80 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {overallUtilization.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className={`${getUtilizationColor(overallUtilization)} h-4 rounded-full transition-all duration-700 ease-out`}
                  style={{ width: `${Math.min(overallUtilization, 100)}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span>0 RSD</span>
                <span>{formatCurrency(overview.totalAllocated)} RSD</span>
              </div>
            </div>
          )}

          {/* Individual Breakdown Table */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Pojedinacni Pregled</h2>
            <InfluencerBudgetTable
              budgets={overview.influencerBudgets}
              onRowClick={handleRowClick}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default AdminBudgetDashboard;
